import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  BenefitCategory,
  useCompanySettingsQuery,
  useSubscriberQuery,
} from 'theSource/queries';
import {
  BenefitDto,
  CategoryResponseDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';
import { selectIsAdmin } from 'theSource/features';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { invalidatePostElectionQueries } from 'enrollment/features/Plans/utils';
import { ROUTES } from 'enrollment/app/routes';
import { queryClient } from 'theSource/utils';
import { GET_BENEFIT_PLANS_QUERY_KEY } from 'enrollment/queries/useBenefitPlansQuery/utils';

export const useSavviOverRideChosenBenefits = (
  benefits?: BenefitDto[],
  bundledOrgBenefitIds?: number[]
): boolean => {
  const isAdmin = useSelector(selectIsAdmin);
  const { companySettings } = useCompanySettingsQuery({
    enabled: true,
  });
  const { subscriber } = useSubscriberQuery();
  const enrollmentContextType = useSelector(selectEnrollmentContext);

  const override =
    companySettings?.data.savvi_bundled_benefits_enabled &&
    (!isAdmin || (isAdmin && subscriber?.data.test_employee?.value === true)) &&
    enrollmentContextType !== 'life_event' &&
    (!benefits || hasSavviBenefits(benefits, bundledOrgBenefitIds));

  return override || false;
};

export const hasSavviBenefits = (
  benefits: BenefitDto[],
  bundledOrgBenefitIds?: number[]
): boolean => {
  if (!benefits || !bundledOrgBenefitIds || bundledOrgBenefitIds.length === 0) {
    return false;
  }
  return benefits.some(
    (benefit) =>
      bundledOrgBenefitIds?.includes(benefit.benefit.org_benefit_id) &&
      benefit.coverage
  );
};

export const useBundledBenefitCategories = (): BenefitCategory[] => {
  const { t } = useTranslation('pages.bundledBenefits');

  return [
    {
      id: 1,
      name: t('chosenBundle'),
      display_order: 1,
      lookup_code: 'chosenBundle',
    },
    {
      id: 2,
      name: t('otherBundle'),
      display_order: 2,
      lookup_code: 'otherBundle',
    },
  ];
};

export const organizeBundledBenefitCategoryBenefits = (
  benefits: BenefitDto[],
  categoryLookup: 'chosenBundle' | 'otherBundle',
  withUnElected = false,
  bundledOrgBenefitIds?: number[]
): BenefitDto[] => {
  if (withUnElected) {
    return benefits?.filter((details: BenefitDto) => {
      const findDisplayPlansCondition =
        categoryLookup === 'chosenBundle'
          ? bundledOrgBenefitIds?.includes(details.benefit.org_benefit_id)
          : !bundledOrgBenefitIds?.includes(details.benefit.org_benefit_id) &&
            details?.benefit.org_benefit_category !== 'survey';

      return findDisplayPlansCondition;
    });
  }
  return benefits?.filter((details: BenefitDto) => {
    const findDisplayPlansCondition =
      categoryLookup === 'chosenBundle'
        ? details.coverage &&
          bundledOrgBenefitIds?.includes(details.benefit.org_benefit_id)
        : details.coverage &&
          details?.benefit.org_benefit_category !== 'survey' &&
          !bundledOrgBenefitIds?.includes(details.benefit.org_benefit_id);

    return findDisplayPlansCondition;
  });
};

interface PostElectionValuesType {
  navPath: string;
  benefitData?: BenefitDto;
  pendedBenefitData?: BenefitDto | undefined;
  benefitScroll?: string;
}

export const handleSavviPostElection = (
  contextLookup: string,
  opyContextId: number,
  benefitsResponse: CategoryResponseDto,
  currentCategory: string,
  orgBenefitId: number
): PostElectionValuesType => {
  invalidatePostElectionQueries(contextLookup, opyContextId, orgBenefitId);
  const benefitsInCategoryWithCov =
    benefitsResponse.data.bundled_org_benefit_ids?.includes(orgBenefitId)
      ? []
      : benefitsResponse.data.benefits;

  const benefitsInCategory = benefitsInCategoryWithCov.filter(
    (benefit) =>
      !benefit.coverage ||
      (benefit.benefit.active_enrollment_required && // skip savvi??? or handled on backend?
        benefitsResponse.data.active_enrollment_benefit_status[
          benefit.benefit.org_benefit_id.toString()
        ] === false)
  );
  const currentIndex = benefitsInCategory.findIndex(
    (benefit) => benefit.benefit.org_benefit_id === orgBenefitId
  );

  const benefitScroll = benefitsInCategoryWithCov.find(
    (benefit) => benefit.benefit.org_benefit_id === orgBenefitId
  )?.benefit.benefit_lookup_code;
  const nextBenefitData = benefitsInCategory[currentIndex + 1];
  const pendedBenefitData =
    benefitsResponse.data.pended_benefits?.[currentIndex + 1];

  if (nextBenefitData) {
    const plugin = nextBenefitData.benefit.benefit_plugin;
    let navPath = `/enrollment${ROUTES.enrollment.plans.path}`;
    if (plugin) {
      navPath = `/enrollment${ROUTES.enrollment.plugin.path}/${plugin.code}`;
    } else if (nextBenefitData.benefit.benefit_class === 'survey') {
      navPath = `/enrollment${ROUTES.enrollment.survey.path}/${nextBenefitData.benefit.org_benefit_id}`;
    }

    //used for benefit plans. this invalidates all plan queries matching this including those with and without dependents - needed if dependent plan viewed out of order
    queryClient.invalidateQueries({
      queryKey: [
        GET_BENEFIT_PLANS_QUERY_KEY +
          '-' +
          nextBenefitData.benefit.org_benefit_id,
      ],
    });

    return {
      navPath: navPath,
      benefitData: nextBenefitData,
      pendedBenefitData,
    };
  } else {
    return {
      navPath: `/enrollment/benefits`,
      benefitScroll,
    };
  }
};
